/**
 * Cache updaters for the lists a view holds: its filters, its filter groups,
 * its sortings and its group-bys.
 *
 * Each of the fifteen events about them is a change to one entry in one of
 * those four lists, and they are all the same change seen four times -- so this
 * is one set of functions over a named list rather than four sets that differ
 * only in a key.
 *
 * **Patching these is the whole of the handler.** Upstream ends every one of
 * them with "and if this is the view on screen, refresh the rows", over an event
 * bus. Here the four lists are exactly what `rowQuerySignature` is built from,
 * so writing the change into the cache changes the signature, and the window
 * refreshes itself. The bus is replaced by the architecture rather than
 * reimplemented.
 *
 * Ported from `web-frontend/modules/database/store/view.js`.
 */
import type {
  View,
  ViewFilter,
  ViewFilterGroup,
  ViewGroupBy,
  ViewSort,
} from '@/api/types'

/**
 * The four lists, and what each holds.
 *
 * Decorations are deliberately not among them -- see the note in `database.ts`
 * on why the three decoration events are not registered.
 */
interface ViewChildren {
  filters?: ViewFilter[]
  filter_groups?: ViewFilterGroup[]
  sortings?: ViewSort[]
  group_bys?: ViewGroupBy[]
}

export type ChildList = keyof ViewChildren

/** Everything in one of the four lists has an id, and that is all this needs. */
interface Child {
  id: number
}

/**
/** The list, or nothing when the view is not carrying it. */
function listOf(view: View, list: ChildList): Child[] | undefined {
  // The one cast in this file. `View` is a union of seven view types; nothing
  // joins a key chosen at runtime back up to that union.
  return (view as ViewChildren)[list]
}

/**
 * Changes one of the four lists, or answers with the view it was given.
 *
 * The two rules every one of these obeys, written once so that all three are
 * the same shape and neither rule can be kept in one of them and forgotten in
 * another.
 *
 * A list the view is not carrying is left alone. Absent is not the same as
 * empty: a view fetched without `include=filters` has no `filters` key at all,
 * and inventing one would put a list into the cache that the next refetch takes
 * away again -- and since three of these four lists are in the row query's
 * signature, that is a window refetching itself every time the views refresh.
 *
 * A change that changes nothing answers with the same object, which is how the
 * handler knows not to write to the cache -- and therefore how a grid avoids
 * re-rendering for an event about a filter it never had.
 */
function change(
  view: View,
  list: ChildList,
  update: (held: readonly Child[]) => Child[],
): View {
  const held = listOf(view, list)
  if (held === undefined) return view

  const next = update(held)
  return next === held ? view : { ...view, [list]: next }
}

/**
 * Adds a child, or replaces the one already held with that id.
 *
 * Upsert rather than insert, for the reason a table's and a view's are: an
 * event can arrive for something this tab already has -- a reconnect replays
 * what was missed, and a replay does not know what was applied.
 */
export function upsertChild<T extends Child>(
  view: View,
  list: ChildList,
  child: T,
): View {
  return change(view, list, (held) => {
    const index = held.findIndex((one) => one.id === child.id)
    if (index === -1) return [...held, child]

    const next = [...held]
    next[index] = child
    return next
  })
}

export function removeChild(view: View, list: ChildList, childId: number): View {
  return change(view, list, (held) => {
    const next = held.filter((one) => one.id !== childId)
    return next.length === held.length ? (held as Child[]) : next
  })
}

/**
 * Renumbers a list's `priority` to match the order the ids arrive in.
 *
 * The event carries the *complete* order -- the backend's `order_objects`
 * returns the full list however partial the request was -- and it numbers from
 * one, which is what this reproduces. A child the event does not mention keeps
 * the priority it had, which is the same defence the table and view orderings
 * make: an event about a subset must not send everything else to the front.
 *
 * Only the relative order reaches the row query, because the signature emits
 * the terms in priority order and leaves the numbers out. So this is about what
 * the sort menu shows, and the refetch it causes is the ordering actually
 * changing.
 */
export function prioritizeChildren(
  view: View,
  list: ChildList,
  order: readonly number[],
): View {
  return change(view, list, (held) =>
    held.map((child) => {
      const index = order.indexOf(child.id)
      return index === -1 ? child : { ...child, priority: index + 1 }
    }),
  )
}
